/**
 * @file AcitivityPhotoGalleryPage.js
 * @module src/js/pages/AcitivityPhotoGalleryPage
 * @description
 * **Page component for displaying and managing activity photos.**
 * - Fetches activity photo data from the backend.
 * - Displays photos in a paginated table format.
 * - Provides search functionality to filter photos.
 * - Allows authorized users to upload, edit, and delete photos.
 *
 * @version
 * <p>
 *  2025-11-29: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-11-30: The "NEW" image added for news within 90 days. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Migrate to useDeleteEntry. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUser } from "../contexts/UserProvider";
import { useTheme } from "../contexts/ThemeProvider";
import useFetchFromBackend from "../hooks/useFetchFromBackend";
import useDeleteEntry from "../hooks/useDeleteEntry";
import NumberPagination from '../components/NumberPagination';
import PagedTable from '../components/PagedTable';
import PhotosUploadForm from '../components/PhotosUploadForm';
import SearchBar from "../components/SearchBar";
import { NewTab } from "../components/UtilityComponents";
import SpanARow from "../components/SpanARow";
import newGif from '../../images/new.gif';

export default function Photos() {
    const { pathname } = useLocation();
    const { uid } = useUser();
    const { theme } = useTheme();
    const fetchFromBackend = useFetchFromBackend();
    const [photos, setPhotos] = useState([]);
    const [filteredPhotos, setFilteredPhotos] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [updatedID, setUpdatedID] = useState(-1);
    const [deleting, setDeleting] = useState(false);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);
    const deleteEntry = useDeleteEntry({ setData: setPhotos, setDeleting });

    const numPageButtons = 10;
    const numItemsPerPage = 15;

    const photosOnCurrentPage = useMemo(() => {
        if (!filteredPhotos || filteredPhotos.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredPhotos.slice(start, end);
    }, [filteredPhotos, currentPage]);
  
    useEffect(() => {
        async function fetchData() {
            const data = await fetchFromBackend({ request: "/api/activity" });
            setPhotos(data || []);
            setLoading(false);
        }
        fetchData();
    }, [fetchFromBackend]);
  
    const pagination = (
        <NumberPagination
            length={filteredPhotos.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            headerRef={headerRef}
        />
    );

    // The reason for using useMemo here is to prevent unnecessary re-renders
    // You can find more details in the News.js component
    const filterKeys = useMemo(() => ['date', 'Title'], []);
    const searchBar = (
        <SearchBar
            data={photos}
            setFilteredData={setFilteredPhotos}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            filterKeys={filterKeys}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className="hori-vert-center" width="15%">Time</th>
                        <th className="hori-vert-center" colSpan={uid !== 'Guest' ? 2 : 1}>Title</th>
                    </tr>
                </thead>
                <tbody>
                    <PhotosUploadForm setData={setPhotos}/>
                    {filteredPhotos.length === 0 && (
                        <SpanARow
                            colSpan={uid === 'Guest' ? 2 : 3}
                            text="No activity photos found"
                            loading={loading}
                        />
                    )}
                    {photosOnCurrentPage.map((item, i) => (
                        item.ActivityID !== updatedID ? (
                            <tr key={i}>
                                <td className="hori-vert-center">
                                    {item.date}<br />
                                    {new Date(item.date) >= new Date(Date.now() - 90 * 24 * 60 * 60 * 1000) && (
                                        <img src={newGif} alt='New' style={{ marginLeft: '5px' }} />
                                    )}
                                </td>
                                <td style={{textAlign: 'left', verticalAlign: 'middle'}}>
                                    <NewTab href={item.Link}>{item.Title}</NewTab>
                                </td>
                                {uid !== 'Guest' && (
                                    <td className='hori-vert-center' width='10%'>
                                        <button
                                            onClick={() => setUpdatedID(item.ActivityID)}
                                            disabled={deleting}
                                            style={{ marginBottom: '15px' }}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            onClick={() => deleteEntry({
                                                request: "/api/activity",
                                                id: item.ActivityID,
                                                from: pathname,
                                            })}
                                            disabled={deleting}
                                            style={{
                                                color: theme === 'dark' ? 'lightcoral' : 'red',
                                                outline: `2px solid ${theme === 'dark' ? 'lightcoral' : 'rgba(255, 0, 0, 0.1)'}`,
                                                outlineOffset: '-2px',
                                            }}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ) : (
                            <PhotosUploadForm
                                key={i}
                                setData={setPhotos}
                                item={item}
                                setUpdatedID={setUpdatedID}
                            />
                        )
                    ))}
                </tbody>
            </table>
        </div>
    );
  
    return (
        <PagedTable
            header="Activity Photo Gallery"
            headerRef={headerRef}
            numberPagination={filteredPhotos.length > numItemsPerPage ? pagination : null}
            table={table}
            searchBar={searchBar}
        />
    );
}
